import * as RadPopover from '@radix-ui/react-popover'
import { useAtom } from '@reatom/npm-react'
import React, { useState } from 'react'
import styled, { css } from 'styled-components'

import { Button } from '@blade/ui-kit'
import { languageAtom, useTranslation } from '@root/shared/localization'
import { DataTableHeaderSorting } from '@root/shared/ui-kit/data-table/ui/data-table-header/data-table-header-sorting'

import type { ExploreSortKey } from '../../atoms/reatom-explore-top-gainers'
import type { Lang, TransKey } from '@root/shared/localization'
import type { ClassNameProps } from '@root/shared/react'

type ExploreSortingSelectProps = {
  options?: Array<ExploreSortKey>
  value: ExploreSortKey
  sortDir: 'asc' | 'desc'
  onSortByChange: (value: ExploreSortKey) => void
  onSortDirToggle: () => void
  open: boolean
  onOpenChange: (open: boolean) => void
} & ClassNameProps

const TRANSLATION_KEYS: Record<ExploreSortKey, TransKey> = {
  holdersCount: 'explore.sort.holders-count',
  txCount: 'explore.sort.tx-count',
  createdAt: 'explore.sort.created-at',
  marketCapUsd: 'trade.market-cap-short',
  gainScore: 'explore.sort.gain-score',
  liquidityInUsd: 'common.liquidity',
  'extraDetails.migration.migrationTimestamp': 'common.liquidity',
  volumeInUsd: 'common.volume',
  netVolumeInUsd: 'common.volume',
  holdersChangePercents: 'explore.sort.holders-change-percent',
  priceChangePercents: 'common.n_a',
}

const SELECT_WIDTH: Partial<Record<Lang.Lang, number>> = {
  en: 75,
  ru: 90,
}
const DEFAULT_SELECT_WIDTH = 90

export const ExploreSortingSelect: React.FC<ExploreSortingSelectProps> = ({
  options,
  value,
  sortDir,
  open,
  onOpenChange,
  onSortByChange,
  onSortDirToggle,
}) => {
  const { t } = useTranslation()
  const [sortingHovered, setSortingHovered] = useState(false)

  const label = t(TRANSLATION_KEYS[value])
  const handleClose = () => onOpenChange(false)
  const [lang] = useAtom(languageAtom)
  const selectWidth = SELECT_WIDTH[lang] ?? DEFAULT_SELECT_WIDTH

  const handleSortDirToggle = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation()
    onSortDirToggle()
  }

  return (
    <Root open={open} onOpenChange={onOpenChange}>
      <Trigger open={open} onClick={handleClose} asChild>
        <SortButton
          $sortingHovered={sortingHovered}
          variant="ghost"
          radius="rounded"
          type="button"
          iconRight={<SortingIcon size={10} sorting={sortDir} />}
          css={css`
            --select-width: ${selectWidth}px;
          `}
        >
          <span>{label}</span>
          <ShadowSortButton
            onMouseEnter={() => setSortingHovered(true)}
            onMouseLeave={() => setSortingHovered(false)}
            aria-hidden={open}
            onClick={handleSortDirToggle}
          />
        </SortButton>
      </Trigger>
      <RadPopover.Portal>
        <Content
          side="bottom"
          align="start"
          css={css`
            --select-width: ${selectWidth}px;
          `}
        >
          <ButtonExpanded
            variant="ghost"
            iconRight={<SortingIcon size={10} sorting={sortDir} />}
            onClick={handleClose}
          >
            <span>{t('common.sort')}</span>
          </ButtonExpanded>
          <Items>
            {options?.map((option) => (
              <Item key={option} onClick={() => onSortByChange(option)}>
                <Label htmlFor={option}>{t(TRANSLATION_KEYS[option])}</Label>
              </Item>
            ))}
          </Items>
        </Content>
      </RadPopover.Portal>
    </Root>
  )
}

const Root = styled(RadPopover.Root)`
  position: relative;
`

const ShadowSortButton = styled.div`
  position: absolute;
  top: -1px;
  right: 0;
  width: 20px;
  height: 20px;
  cursor: pointer;

  &[aria-hidden='true'] {
    display: none;
  }
`

const Trigger = styled(RadPopover.Trigger)<{
  open: boolean
}>``

const Content = styled(RadPopover.Content)`
  width: var(--select-width);
  border: 1px solid var(--background-5);

  backdrop-filter: blur(35px);
  border-radius: 6px;
  animation-duration: 400ms;
  animation-timing-function: cubic-bezier(0.16, 1, 0.3, 1);

  margin-top: -21px;
`

const Items = styled.div`
  padding: 4px 6px 4px;
  gap: 4px;
  display: grid;
`

const Item = styled.div`
  width: var(--select-width);
  color: var(--text-11);

  font-size: 11px;
  font-weight: 500;
  line-height: 16px;
  transition: all 100ms ease-in;

  &:hover {
    color: var(--text-12);
    cursor: pointer;
  }
`

const Label = styled.label`
  cursor: pointer;
`

const SortButton = styled(Button)<{ $sortingHovered?: boolean }>`
  width: var(--select-width);
  border: 1px solid var(--background-5);
  --button-radius-rounded: 6px;

  position: relative;

  height: 20px;
  font-size: 11px;
  font-weight: 600;
  padding-inline: 5px 2px;

  display: grid;
  grid-template-columns: 1fr auto;

  justify-items: start;
  justify-content: start;
  text-overflow: ellipsis;
  white-space: nowrap;

  transition: all 100ms ease-in;

  ${({ $sortingHovered }) =>
    $sortingHovered &&
    css`
      &:hover {
        background: transparent;
        color: var(--text-11);
      }
    `}

  > span {
    text-align: left;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`

const ButtonExpanded = styled(SortButton)`
  --button-radius-full: 0;
  border: 1px solid transparent;
  background: transparent;

  width: 100%;
  cursor: pointer;
  border-bottom: 1px solid var(--background-4);

  padding-inline: 5px 1px;

  &:hover {
    background: transparent;
  }
`

const SortingIcon = styled(DataTableHeaderSorting)`
  width: 12px;
`
